import type { paths, Schemas } from '@kanos/api-client';
import { api, failure } from '@kanos/web-core';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { keys, useFirmMutation } from './queries';

export type FirmPortalLegal = Schemas['FirmPortalLegal'];
export type Inquiry = Schemas['Inquiry'];
export type ClientEntry = Schemas['ClientEntry'];
export type ClientQuery = NonNullable<paths['/api/v1/firm/clients']['get']['parameters']['query']>;
export type InquiryQuery = NonNullable<
  paths['/api/v1/firm/inquiries']['get']['parameters']['query']
>;

type TargetProfile = Schemas['TargetProfile'];

/** Whom the firm takes on (ADR-068), `null` while no profile is set. */
export const targetProfileQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...keys.all(tenantId), 'target-profile'] as const,
    queryFn: async (): Promise<TargetProfile | null> => {
      const { data, error, response } = await api.GET('/api/v1/firm/target-profile');
      if (!data) throw failure('getTargetProfile', error, response.status);
      return data.profile;
    },
  });

export const useSetTargetProfile = (tenantId: string) =>
  useFirmMutation(tenantId, async (body: TargetProfile) => {
    const { data, error, response } = await api.PUT('/api/v1/firm/target-profile', { body });
    if (!data) throw failure('setTargetProfile', error, response.status);
    return data.profile;
  });

/** The firm's legal pages its client portal links to (ADR-023). */
export const portalLegalQuery = (tenantId: string) =>
  queryOptions({
    queryKey: [...keys.all(tenantId), 'portal-legal'] as const,
    queryFn: async (): Promise<FirmPortalLegal> => {
      const { data, error, response } = await api.GET('/api/v1/firm/portal-legal');
      if (!data) throw failure('getFirmPortalLegal', error, response.status);
      return data;
    },
  });

export const useSetPortalLegal = (tenantId: string) =>
  useFirmMutation(tenantId, async (legal: FirmPortalLegal): Promise<FirmPortalLegal> => {
    const { data, error, response } = await api.PUT('/api/v1/firm/portal-legal', {
      body: legal,
    });
    if (!data) throw failure('setFirmPortalLegal', error, response.status);
    return data;
  });

/** One page of the inquiries of prospective clients, as the table shows it. */
export const inquiriesQuery = (tenantId: string, query: InquiryQuery) =>
  queryOptions({
    queryKey: [...keys.all(tenantId), 'inquiries', query] as const,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/v1/firm/inquiries', {
        params: { query },
      });
      if (!data) throw failure('listInquiries', error, response.status);
      return data;
    },
    placeholderData: keepPreviousData,
  });

/** One page of the overview of prospects, intakes and clients (ADR-037). */
export const clientsQuery = (tenantId: string, query: ClientQuery) =>
  queryOptions({
    queryKey: [...keys.all(tenantId), 'clients', query] as const,
    queryFn: async () => {
      const { data, error, response } = await api.GET('/api/v1/firm/clients', {
        params: { query },
      });
      if (!data) throw failure('listClients', error, response.status);
      return data;
    },
    placeholderData: keepPreviousData,
  });

/** The firm has answered: closed for good, and the notice that it came in with it. */
export const useCloseInquiry = (tenantId: string) =>
  useFirmMutation(tenantId, async (inquiryId: string): Promise<Inquiry> => {
    const { data, error, response } = await api.POST('/api/v1/firm/inquiries/{inquiryId}/close', {
      params: { path: { inquiryId } },
    });
    if (!data) throw failure('closeInquiry', error, response.status);
    return data;
  });
